import { isPhotoFile, isVideoFile } from "./chatVideo";

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.82;
const SKIP_IF_UNDER_BYTES = 350 * 1024;
const POOL = 3;

const isPdfLike = (file) =>
    !!(file && (file.isPdf || file.fileType === "doc" || file.type === "application/pdf"
        || /\.pdf(\?|$)/i.test(file.name || file.originalName || file.attachedFileName || "")));

export const isLocalArticleImage = (file) =>
    !!(
        file
        && file.local
        && !isVideoFile(file)
        && !isPdfLike(file)
        && (
            file.fileType === "image"
            || isPhotoFile(file)
            || /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(file.name || file.originalName || "")
        )
    );

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("image"));
        img.src = src;
    });

const canvasToBlob = (canvas) =>
    new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY);
    });

const copyArticleFields = (from, to) => {
    to.local = true;
    to.fileType = "image";
    to.originalName = from.originalName || to.name;
    to.attachedFileName = to.name;
    to.clientId = from.clientId;
    to.url = from.url;
    to.fileUrl = from.fileUrl || from.url;
    to.serverUrl = from.serverUrl;
    return to;
};

export async function compressArticleImage(file) {
    if (!isLocalArticleImage(file)) return file;
    if (typeof file.size === "number" && file.size <= SKIP_IF_UNDER_BYTES) return file;
    if (typeof document === "undefined") return file;

    const ownsUrl = !(file.url && (String(file.url).startsWith("blob:") || String(file.url).startsWith("data:")));
    const srcUrl = ownsUrl ? URL.createObjectURL(file) : file.url;
    try {
        const img = await loadImage(srcUrl);
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        if (!width || !height) return file;
        const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
        if (scale >= 0.98 && file.size <= 800 * 1024) return file;

        const canvas = document.createElement("canvas");
        canvas.width = Math.max(2, Math.round(width * scale));
        canvas.height = Math.max(2, Math.round(height * scale));
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) return file;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const blob = await canvasToBlob(canvas);
        if (!blob || blob.size >= file.size * 0.92) return file;

        const baseName = String(file.name || file.originalName || "image").replace(/\.[^.]+$/, "");
        const compressed = new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
        return copyArticleFields(file, compressed);
    } catch (_) {
        return file;
    } finally {
        if (ownsUrl) {
            try { URL.revokeObjectURL(srcUrl); } catch (_) { /* ignore */ }
        }
    }
}

async function mapPool(items, limit, mapper) {
    const results = new Array(items.length);
    let next = 0;
    const worker = async () => {
        while (next < items.length) {
            const i = next++;
            results[i] = await mapper(items[i], i);
        }
    };
    const n = Math.max(1, Math.min(limit, items.length || 1));
    await Promise.all(Array.from({ length: n }, worker));
    return results;
}

export async function compressLocalArticleImages(files, { onProgress } = {}) {
    const list = Array.isArray(files) ? files : [];
    if (list.length === 0) return list;
    const compressible = list
        .map((file, index) => ({ file, index }))
        .filter(({ file }) => isLocalArticleImage(file));
    if (compressible.length === 0) {
        onProgress?.(100);
        return list;
    }
    let done = 0;
    const compressedByIndex = {};
    await mapPool(compressible, POOL, async ({ file, index }) => {
        compressedByIndex[index] = await compressArticleImage(file);
        done += 1;
        onProgress?.(Math.round((done / compressible.length) * 100));
    });
    onProgress?.(100);
    return list.map((file, index) => compressedByIndex[index] || file);
}
